import { NotEnoughSeatsDialog } from "features/subscriptions/components";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Campaign } from "common/types";

import useCampaignActions from "../../hooks/useCampaignActions";
import useCampaignValidation from "../../hooks/useCampaignValidation";
import AssignSeats from "../AssignSeats";
import CampaignScheduling from "../CampaignScheduling";
import CampaignNameEdit from "./CampaignNameEdit";
import DeleteCampaign from "./DeleteCampaign";
import DuplicateCampaign from "./DuplicateCampaign";

import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { CampaignActionType, DetailedCampaign } from "../../types";

interface CampaignActionsDialogProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  actionType: CampaignActionType;
  campaign: DetailedCampaign | Campaign;
}

export default function CampaignActionsDialog({
  isOpen,
  setIsOpen,
  actionType,
  campaign,
}: CampaignActionsDialogProps) {
  const { id, owners, outreach_type: outreachType, name } = campaign;

  const isDesktop = useTwBreakpoint("lg");

  function onClose() {
    setIsOpen(false);
  }

  const { updateCampaignState, onUnscheduleCampaign, isActionPending } =
    useCampaignActions(campaign, onClose);

  const { workerAccountsWithoutSeat } = useCampaignValidation(owners);

  if (actionType === "license") {
    return (
      <NotEnoughSeatsDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        noSeatAccountIds={workerAccountsWithoutSeat.map(
          ({ id: accountId }) => accountId,
        )}
        mixpanelSource="upsell_campaign"
      />
    );
  }

  let mainContent: ReactNode;
  if (actionType === "delete") {
    mainContent = (
      <DeleteCampaign
        campaignId={id}
        campaignName={name}
        onClose={() => setIsOpen(false)}
        onCancel={() => setIsOpen(false)}
      />
    );
  } else if (actionType === "duplicate") {
    mainContent = (
      <DuplicateCampaign
        campaignId={id}
        accountIds={owners}
        name={name}
        isDrip={outreachType === "drip"}
        onCancel={onClose}
      />
    );
  } else if (actionType === "editName") {
    mainContent = (
      <CampaignNameEdit campaignName={name} campaignId={id} onClose={onClose} />
    );
  } else if (
    (actionType === "start" || actionType === "schedule") &&
    !!workerAccountsWithoutSeat.length
  ) {
    mainContent = (
      <AssignSeats
        accounts={workerAccountsWithoutSeat}
        onClose={onClose}
        onSuccess={onClose}
      />
    );
  } else if (actionType === "schedule") {
    mainContent = (
      <CampaignScheduling
        campaignId={id}
        onCancel={onClose}
        onSuccess={onClose}
      />
    );
  } else {
    const Footer = isDesktop ? DialogFooter : DrawerFooter;
    const Header = isDesktop ? DialogHeader : DrawerHeader;

    let onClick: () => void;

    if (actionType === "unschedule") {
      onClick = onUnscheduleCampaign;
    } else {
      onClick = updateCampaignState;
    }

    mainContent = (
      <>
        <Header
          title={<>{actionType} campaign</>}
          subtitle={
            <>
              Are you sure you want to {actionType} campaign &quot;
              {name}
              &quot;?
            </>
          }
        />

        <Footer className="items-center justify-end">
          <Button
            variant="secondary-black"
            onClick={onClose}
            disabled={isActionPending}
          >
            Cancel
          </Button>

          <Button
            onClick={onClick}
            className="capitalize"
            isLoading={isActionPending}
          >
            {actionType}
          </Button>
        </Footer>
      </>
    );
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  return (
    <Component open={isOpen} onOpenChange={setIsOpen}>
      <Content
        size={
          actionType === "schedule" || actionType === "duplicate" ? "lg" : "md"
        }
      >
        {mainContent}
      </Content>
    </Component>
  );
}
